import React, { useState } from 'react'
import { formatCurrency, formatDate, formatDateTime } from '../../utils/api'
import { api } from '../../utils/api'
import { SHOPIFY_ADMIN_URL } from '../../utils/constants'
import { FulfillmentBadge, FinancialBadge } from './StatusBadge'
import PinButton from './PinButton'

function OrderDetailModal({ order, onClose, onProductClick, loadingProduct, onOrderUpdate }) {
  const [showTicketForm, setShowTicketForm] = useState(false)
  const [ticketSubject, setTicketSubject] = useState('')
  const [ticketMessage, setTicketMessage] = useState('')
  const [sendingTicket, setSendingTicket] = useState(false)
  const [ticketSent, setTicketSent] = useState(false)

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleString('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short'
    })
  }

  const isUnfulfilled = !order.fulfillment_status || order.fulfillment_status === 'unfulfilled'
  const isNotCancelled = !order.cancelled_at && order.financial_status !== 'refunded'
  const canCancel = isUnfulfilled && isNotCancelled
  const isPaid = ['paid', 'partially_paid'].includes(order.financial_status)
  const orderTotal = parseFloat(order.total_price) || 0

  const customerEmail = order.customer?.email || order.email
  const customerName = [order.customer?.first_name, order.customer?.last_name].filter(Boolean).join(' ')
  const isShopifyOrder = (order.name || '').startsWith('AT')
  const canEmailCustomer = isShopifyOrder && customerEmail

  // Already refunded amount (from Shopify refunds array)
  const alreadyRefunded = (order.refunds || []).reduce((sum, r) => {
    return sum + (r.transactions || [])
      .filter(t => t.kind === 'refund' && t.status === 'success')
      .reduce((s, t) => s + parseFloat(t.amount || 0), 0)
  }, 0)

  function openTicketForm() {
    setTicketSubject(`Re: Order ${order.name || '#' + order.order_number}`)
    setTicketMessage('')
    setShowTicketForm(true)
    setTicketSent(false)
  }

  function closeTicketForm() {
    setShowTicketForm(false)
    setTicketSubject('')
    setTicketMessage('')
  }

  async function sendTicket() {
    if (!ticketSubject.trim() || !ticketMessage.trim()) {
      alert('Please enter both a subject and message.')
      return
    }

    setSendingTicket(true)
    try {
      await api.post('/api/tickets/create-from-order', {
        orderId: String(order.id),
        orderName: order.name || `#${order.order_number}`,
        toEmail: customerEmail,
        toName: customerName,
        subject: ticketSubject.trim(),
        message: ticketMessage.trim()
      })
      setTicketSent(true)
      setTimeout(() => {
        closeTicketForm()
        setTicketSent(false)
      }, 2000)
    } catch (error) {
      alert(`Failed to send email: ${error.message}`)
    } finally {
      setSendingTicket(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-order-detail" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Order Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <PinButton
              entityType="order"
              entityId={order.id || order.order_id}
              entityName={`Order #${order.order_number || order.name || order.id}`}
            />
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>
        </div>

        <div className="modal-body-scroll">
          {/* Warning: fulfillment reverted — remind to cancel shipping label */}
          {canCancel && order.fulfillments?.length > 0 && (
            <div style={{ padding: '10px 14px', marginBottom: 16, borderRadius: 6, background: '#fef3c7', border: '1px solid #fde68a', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ fontSize: 16, lineHeight: 1 }}>&#9888;</span>
              <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
                <strong>Shipping label may still be active.</strong> This order was previously marked as fulfilled. If you cancel, remember to also cancel the shipping label with the carrier to avoid being charged.
              </div>
            </div>
          )}

          {/* Order Information */}
          <div className="order-detail-section">
            <h3>Order Information</h3>
            <div className="order-detail-grid">
              <div className="order-detail-item">
                <span className="order-detail-label">Order ID</span>
                <span className="order-detail-value">{order.id || '—'}</span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Order Number</span>
                <span className="order-detail-value">#{order.order_number || order.name || '—'}</span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Status</span>
                <span className="order-detail-value">
                  <FulfillmentBadge status={order.fulfillment_status} />
                </span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Financial Status</span>
                <span className="order-detail-value">
                  <FinancialBadge status={order.financial_status} />
                </span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Marketplace</span>
                <span className="order-detail-value">{order.source_name || '—'}</span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Order Date</span>
                <span className="order-detail-value">{formatDateDisplay(order.created_at)}</span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Updated</span>
                <span className="order-detail-value">{formatDateDisplay(order.updated_at)}</span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Total</span>
                <span className="order-detail-value order-total">
                  {formatCurrency(order.total_price)}
                  {order.currency && order.currency !== 'GBP' && ` ${order.currency}`}
                </span>
              </div>
            </div>
          </div>

          {/* Previous Refunds */}
          {order.refunds && order.refunds.length > 0 && (
            <div className="order-detail-section">
              <h3>Previous Refunds ({order.refunds.length})</h3>
              {order.refunds.map((refund, idx) => {
                const refundTotal = (refund.transactions || [])
                  .filter(t => t.kind === 'refund')
                  .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0)
                return (
                  <div key={idx} style={{ padding: '8px 12px', background: '#f0fdf4', borderRadius: 6, marginBottom: 6, fontSize: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600, color: '#065f46' }}>{formatCurrency(refundTotal)}</span>
                      <span className="text-muted">{formatDateDisplay(refund.created_at)}</span>
                    </div>
                    {refund.note && <div style={{ color: '#6b7280', marginTop: 4 }}>{refund.note}</div>}
                    {refund.refund_line_items && refund.refund_line_items.length > 0 && (
                      <div style={{ color: '#6b7280', marginTop: 4 }}>
                        {refund.refund_line_items.map((ri, i) => {
                          const lineItem = order.line_items?.find(li => li.id === ri.line_item_id)
                          return <div key={i}>{lineItem?.title || `Item ${ri.line_item_id}`} x{ri.quantity}</div>
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
              {alreadyRefunded > 0 && (
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
                  Total refunded: <strong>{formatCurrency(alreadyRefunded)}</strong> of {formatCurrency(orderTotal)}
                  {remainingRefundable > 0 && <> — <strong>{formatCurrency(remainingRefundable)}</strong> remaining</>}
                </div>
              )}
            </div>
          )}

          {/* Customer */}
          <div className="order-detail-section">
            <h3>Customer</h3>
            <div className="order-detail-grid">
              <div className="order-detail-item">
                <span className="order-detail-label">Name</span>
                <span className="order-detail-value">
                  {order.customer?.first_name || ''} {order.customer?.last_name || '—'}
                </span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Email</span>
                <span className="order-detail-value">{order.customer?.email || order.email || '—'}</span>
              </div>
            </div>
          </div>

          {/* Shipping */}
          <div className="order-detail-section">
            <h3>Shipping</h3>
            <div className="order-detail-grid">
              <div className="order-detail-item">
                <span className="order-detail-label">Tracking Number</span>
                <span className="order-detail-value">
                  {order.fulfillments?.[0]?.tracking_number || '—'}
                </span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Tracking Company</span>
                <span className="order-detail-value">
                  {order.fulfillments?.[0]?.tracking_company || '—'}
                </span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Tracking URL</span>
                <span className="order-detail-value">
                  {order.fulfillments?.[0]?.tracking_url ? (
                    <a
                      href={order.fulfillments[0].tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link"
                    >
                      View Tracking
                    </a>
                  ) : '—'}
                </span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Shipping Address</span>
                <span className="order-detail-value">
                  {order.shipping_address ? (
                    <>
                      {order.shipping_address.address1}<br />
                      {order.shipping_address.address2 && <>{order.shipping_address.address2}<br /></>}
                      {order.shipping_address.city}, {order.shipping_address.province_code} {order.shipping_address.zip}<br />
                      {order.shipping_address.country}
                    </>
                  ) : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="order-detail-section">
            <h3>Line Items ({order.line_items?.length || 0})</h3>
            {order.line_items && order.line_items.length > 0 ? (
              <table className="table table-compact">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>SKU</th>
                    <th style={{ textAlign: 'center' }}>Qty</th>
                    <th style={{ textAlign: 'right' }}>Price</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {order.line_items.map((item, idx) => (
                    <tr key={idx}>
                      <td>
                        {item.product_id && onProductClick ? (
                          <button
                            className="link-button"
                            onClick={() => onProductClick(item.product_id)}
                            disabled={loadingProduct}
                          >
                            {loadingProduct ? 'Loading...' : item.title}
                          </button>
                        ) : (
                          item.title
                        )}
                        {item.variant_title && (
                          <span className="text-muted text-sm"> - {item.variant_title}</span>
                        )}
                      </td>
                      <td className="text-muted">{item.sku || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{item.quantity}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(item.price)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {formatCurrency(parseFloat(item.price) * item.quantity)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-muted">No line items</p>
            )}
          </div>

          {/* Order Notes */}
          {order.note && (
            <div className="order-detail-section">
              <h3>Order Notes</h3>
              <p className="order-note">{order.note}</p>
            </div>
          )}

          {/* Email Customer Form */}
          {showTicketForm && (
            <div className="order-detail-section" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
              <h3 style={{ marginTop: 0 }}>Email Customer</h3>
              {ticketSent ? (
                <div style={{ padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, color: '#166534', fontWeight: 500 }}>
                  Email sent and ticket created successfully.
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>To</label>
                    <input
                      type="text"
                      className="form-input"
                      value={customerEmail}
                      disabled
                      style={{ background: '#f1f5f9' }}
                    />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Subject</label>
                    <input
                      type="text"
                      className="form-input"
                      value={ticketSubject}
                      onChange={e => setTicketSubject(e.target.value)}
                    />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Message</label>
                    <textarea
                      className="form-input"
                      rows={6}
                      value={ticketMessage}
                      onChange={e => setTicketMessage(e.target.value)}
                      placeholder="Write your message to the customer..."
                      style={{ minHeight: 120, resize: 'vertical' }}
                      autoFocus
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button className="btn btn-secondary" onClick={closeTicketForm} disabled={sendingTicket}>
                      Cancel
                    </button>
                    <button className="btn btn-primary" onClick={sendTicket} disabled={sendingTicket}>
                      {sendingTicket ? 'Sending...' : 'Send Email'}
                    </button>
                  </div>
                  <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, marginBottom: 0 }}>
                    This will send an email to the customer and create a ticket in the ticket system.
                  </p>
                </>
              )}
            </div>
          )}

        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {canEmailCustomer && !showTicketForm && (
            <button className="btn btn-moreinfo" onClick={openTicketForm}>
              Email Customer
            </button>
          )}
          {order.id && (
            <a
              href={`${SHOPIFY_ADMIN_URL}/orders/${order.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              View in Shopify
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

export default OrderDetailModal
